import { v4 } from "uuid";
import ReactJson from "react-json-view";
import deepmerge from "deepmerge";
import { FormattedMessage, useIntl } from "react-intl";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useProperty } from "../../components/property-provider";
import schema from "./infrastructure/engineMultiConfig.schema.json";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import SimpleCrud, { defaultCrudMessages } from "../shared/crud/simple-crud";
import { engineMultiConfigMessages, tableHeaderMessages } from "./messages";
import { EngineMultiConfigEntity } from "./domain/engineMultiConfig.entity";
import { SECTION } from "../../lib/constants";
import getTableColumns from "./tableColumns";
import { FormConfiguration } from "../shared/crud/form";
import { ArrayAsString } from "../shared/widgets/arrayAsString";
import { CustomJsonEditor } from "./components/customJsonEditor";
import {
  createEngineMultiConfig,
  deleteEngineMultiConfig,
  getAllEngineMultiConfigs,
  getOneEngineMultiConfig,
  updateEngineMultiConfig,
  getEngineV21AggregateSerializedData,
  validateEngineV21AggregateSerializedData,
} from "./infrastructure/engineMultiConfig.api";

const messages = {
  ...defaultCrudMessages,
  ...engineMultiConfigMessages,
  tableHeaders: tableHeaderMessages,
};

function EngineMultiConfigForm() {
  const intl = useIntl();
  const propertyInfo = useProperty();

  const [defaultSerialized, setDefaultSerialized] = useState<string>();

  const tableColumns = useMemo(() => getTableColumns(intl), [intl]);
  const formConfiguration: FormConfiguration<EngineMultiConfigEntity> = useMemo(() => ({
    __fieldOrder: ["ticker", "triggersList", "serialized", "*"],
    __useProtoStyleArrays: true,
    id: {
      default: () => v4(),
      hidden: true,
    },
    ticker: {
      messages: {
        label: messages.ticker,
      },
      readonly: isNew => !isNew,
    },
    triggersList: {
      fieldWidget: ArrayAsString,
      default: [],
      messages: {
        label: messages.triggers,
      },
    },
    serialized: {
      fieldWidget: (props) => (
        <CustomJsonEditor
          {...props}
          helperReferenceTitle={<FormattedMessage {...messages.jsonReference} />}
          helperReference={
            <ReactJson src={JSON.parse(defaultSerialized || "{}")} collapsed={1} theme="shapeshifter:inverted" />
          }
        />
      ),
      transform: (value: string) => JSON.parse(value),
      messages: {
        label: messages.engineMultiConfigJsonLabel,
      },
    },
  }), [defaultSerialized]);

  const validate = useCallback(async (entity: EngineMultiConfigEntity) => {
    const merged = deepmerge(JSON.parse(defaultSerialized || "{}"), entity.serialized);
    await validateEngineV21AggregateSerializedData(propertyInfo.token, JSON.stringify(merged));
  }, [defaultSerialized, propertyInfo.token]);

  const handleGetAll = useCallback(async () => {
    return getAllEngineMultiConfigs(propertyInfo.token);
  }, [propertyInfo.token]);

  const handleCreate = useCallback(async (entity: EngineMultiConfigEntity) => {
    await validate(entity);
    await createEngineMultiConfig(entity, propertyInfo.token);
    return "success";
  }, [propertyInfo.token, validate]);

  const handleGetOne = useCallback(async (id: string) => {
    return getOneEngineMultiConfig(id, propertyInfo.token);
  }, [propertyInfo.token]);

  const handleUpdate = useCallback(async (entity: EngineMultiConfigEntity) => {
    await validate(entity);
    await updateEngineMultiConfig(entity, propertyInfo.token);
  }, [propertyInfo.token, validate]);

  const handleDelete = useCallback((id: string) => {
    return deleteEngineMultiConfig(id, propertyInfo.token);
  }, [propertyInfo.token]);

  useEffect(() => {
    getEngineV21AggregateSerializedData(propertyInfo.token).then((data) => setDefaultSerialized(data));
  }, [propertyInfo.token]);

  if (!defaultSerialized) {
    return <div></div>;
  }

  return (
    <SimpleCrud<EngineMultiConfigEntity>
      managerName={"engineMultiConfig"}
      formConfiguration={formConfiguration}
      schema={schema}
      tableColumns={tableColumns}
      listHeight={window.innerHeight - 250}
      section={SECTION.ENGINE_MULTI_CONFIG}
      onGetAll={handleGetAll}
      onGetOne={handleGetOne}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      onCreate={handleCreate}
      hideExportButton={true}
      messages={messages}
    />
  );
}

export default withAuthorizationVerifier(EngineMultiConfigForm);
